<template>
  <div class="dashboard">
    <!-- Cabecera -->
    <div class="dash-cabecera">
      <div>
        <h1 class="dash-titulo">Panel de Control</h1>
        <p class="dash-subtitulo">Resumen operativo del sistema SICECOM</p>
      </div>
      <button class="btn btn--secundario btn--xs" @click="cargarDatos" :disabled="cargando">
        <i class="bi" :class="cargando ? 'bi-arrow-clockwise spin' : 'bi-arrow-clockwise'"></i>
        Actualizar
      </button>
    </div>

    <div v-if="error" class="alerta alerta-error">
      <i class="bi bi-exclamation-triangle-fill"></i> {{ error }}
    </div>

    <!-- KPIs principales -->
    <div class="kpis-grid">
      <div class="kpi">
        <div class="kpi__icono kpi__icono--azul"><i class="bi bi-building"></i></div>
        <div class="kpi__info">
          <span class="kpi__valor">{{ kpis.empresas_activas ?? '-' }}</span>
          <span class="kpi__etiqueta">Empresas activas</span>
        </div>
      </div>
      <div class="kpi">
        <div class="kpi__icono kpi__icono--verde"><i class="bi bi-check-circle"></i></div>
        <div class="kpi__info">
          <span class="kpi__valor">{{ kpis.suscripciones_activas ?? '-' }}</span>
          <span class="kpi__etiqueta">Suscripciones activas</span>
        </div>
      </div>
      <div class="kpi">
        <div class="kpi__icono kpi__icono--morado"><i class="bi bi-cash-stack"></i></div>
        <div class="kpi__info">
          <span class="kpi__valor">${{ formatear(kpis.ingresos_mes) }}</span>
          <span class="kpi__etiqueta">
            Ingresos del mes
            <span v-if="kpis.variacion_ingresos != null" class="kpi__variacion" :class="kpis.variacion_ingresos >= 0 ? 'positivo' : 'negativo'">
              <i :class="kpis.variacion_ingresos >= 0 ? 'bi-arrow-up' : 'bi-arrow-down'" class="bi"></i>
              {{ Math.abs(kpis.variacion_ingresos) }}%
            </span>
          </span>
        </div>
      </div>
      <div class="kpi kpi--alerta" :class="{ 'kpi--con-alerta': kpis.pagos_pendientes > 0 }">
        <div class="kpi__icono kpi__icono--amarillo"><i class="bi bi-clock-history"></i></div>
        <div class="kpi__info">
          <span class="kpi__valor">{{ kpis.pagos_pendientes ?? '-' }}</span>
          <span class="kpi__etiqueta">Pagos pendientes</span>
        </div>
      </div>
      <div class="kpi" :class="{ 'kpi--con-alerta': kpis.vencen_7_dias > 0 }">
        <div class="kpi__icono kpi__icono--rojo"><i class="bi bi-calendar-x"></i></div>
        <div class="kpi__info">
          <span class="kpi__valor">{{ kpis.vencen_7_dias ?? '-' }}</span>
          <span class="kpi__etiqueta">Vencen en 7 dias</span>
        </div>
      </div>
      <div class="kpi" :class="{ 'kpi--con-alerta': kpis.vencen_15_dias > 0 }">
        <div class="kpi__icono kpi__icono--naranja"><i class="bi bi-calendar-event"></i></div>
        <div class="kpi__info">
          <span class="kpi__valor">{{ kpis.vencen_15_dias ?? '-' }}</span>
          <span class="kpi__etiqueta">Vencen en 15 dias</span>
        </div>
      </div>
    </div>

    <!-- Fila: alertas + empresas por vencer -->
    <div class="seccion-fila">
      <!-- Alertas -->
      <div class="tarjeta" v-if="alertas.length > 0">
        <div class="tarjeta__cab">
          <i class="bi bi-bell-fill texto-warning"></i>
          <h2 class="tarjeta__titulo">Alertas ({{ alertas.length }})</h2>
        </div>
        <div class="alertas-lista">
          <div v-for="alerta in alertas" :key="alerta.tipo + alerta.ruc" class="alerta-item" :class="'alerta-item--' + alerta.nivel">
            <i class="bi" :class="iconoAlerta(alerta.nivel)"></i>
            <span>{{ alerta.mensaje }}</span>
          </div>
        </div>
      </div>

      <!-- Empresas por vencer -->
      <div class="tarjeta">
        <div class="tarjeta__cab">
          <i class="bi bi-calendar-range texto-warning"></i>
          <h2 class="tarjeta__titulo">Por vencer (15 dias)</h2>
        </div>
        <div v-if="!empresasPorVencer.length" class="texto-vacio">Sin vencimientos proximos</div>
        <table v-else class="tabla">
          <thead>
            <tr>
              <th>Empresa</th>
              <th>Vence</th>
              <th>Ciclo</th>
              <th>Plan</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="emp in empresasPorVencer" :key="emp.ruc">
              <td>
                <div class="tabla__empresa">{{ emp.razon_social }}</div>
                <div class="tabla__ruc">{{ emp.ruc }}</div>
              </td>
              <td>
                <span class="fecha-badge" :class="diasRestantesCss(emp.dias_restantes)">
                  {{ emp.fecha_fin }} ({{ emp.dias_restantes }}d)
                </span>
              </td>
              <td>{{ emp.ciclo_pago }}</td>
              <td>{{ emp.plan_nombre || '-' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Fila: distribucion por plan + ultimos pagos -->
    <div class="seccion-fila">
      <!-- Distribucion por plan -->
      <div class="tarjeta">
        <div class="tarjeta__cab">
          <i class="bi bi-pie-chart texto-primario"></i>
          <h2 class="tarjeta__titulo">Distribucion por plan</h2>
        </div>
        <div v-if="!distribucionPlanes.length" class="texto-vacio">Sin datos</div>
        <div v-else class="distribucion-planes">
          <div v-for="item in distribucionPlanes" :key="item.plan_id" class="plan-barra">
            <div class="plan-barra__info">
              <span class="plan-barra__nombre">{{ item.plan_nombre }}</span>
              <span class="plan-barra__total">{{ item.total_activos }} activas</span>
            </div>
            <div class="barra-contenedor">
              <div
                class="barra-relleno"
                :style="{ width: porcentajePlan(item) + '%', background: 'var(--primary-main)' }"
              ></div>
            </div>
            <div class="plan-barra__detalle">
              <span>Costo: ${{ formatear(item.costo) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Ultimos pagos -->
      <div class="tarjeta">
        <div class="tarjeta__cab">
          <i class="bi bi-credit-card texto-verde"></i>
          <h2 class="tarjeta__titulo">Ultimos pagos</h2>
        </div>
        <div v-if="!ultimosPagos.length" class="texto-vacio">Sin pagos registrados</div>
        <table v-else class="tabla">
          <thead><tr><th>Empresa</th><th>Monto</th><th>Estado</th><th>Fecha</th></tr></thead>
          <tbody>
            <tr v-for="pago in ultimosPagos" :key="pago.id">
              <td>
                <div class="tabla__empresa">{{ pago.razon_social }}</div>
                <div class="tabla__ruc">{{ pago.ruc }}</div>
              </td>
              <td class="tabla__monto">${{ formatear(pago.monto) }}</td>
              <td>
                <span class="estado-chip" :class="estadoPagoCss(pago.estado)">
                  {{ pago.estado }}
                </span>
              </td>
              <td class="tabla__fecha">{{ pago.fecha_pago }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Fila: grafica ingresos + ultimas empresas -->
    <div class="seccion-fila">
      <!-- Grafica de ingresos (barras CSS) -->
      <div class="tarjeta">
        <div class="tarjeta__cab">
          <i class="bi bi-bar-chart-line texto-morado"></i>
          <h2 class="tarjeta__titulo">Ingresos ultimos 6 meses</h2>
        </div>
        <div v-if="!ingresosGrafica.length" class="texto-vacio">Sin datos</div>
        <div v-else class="grafica-barras">
          <div v-for="mes in ingresosGrafica" :key="mes.mes" class="grafica-col">
            <div class="grafica-barra-wrap">
              <span class="grafica-valor">${{ formatearCorto(mes.total) }}</span>
              <div
                class="grafica-barra"
                :style="{ height: alturaBarra(mes.total) + '%' }"
                :title="'$' + formatear(mes.total)"
              ></div>
            </div>
            <span class="grafica-mes">{{ mes.mes_label }}</span>
          </div>
        </div>
      </div>

      <!-- Ultimas empresas -->
      <div class="tarjeta">
        <div class="tarjeta__cab">
          <i class="bi bi-building-add texto-azul"></i>
          <h2 class="tarjeta__titulo">Ultimas empresas registradas</h2>
        </div>
        <div v-if="!ultimasEmpresas.length" class="texto-vacio">Sin registros</div>
        <div v-else class="empresas-lista">
          <div v-for="emp in ultimasEmpresas" :key="emp.ruc" class="empresa-item">
            <div class="empresa-item__avatar" :style="{ background: colorAvatar(emp.razon_social) }">
              {{ iniciales(emp.razon_social) }}
            </div>
            <div class="empresa-item__info">
              <span class="empresa-item__nombre">{{ emp.razon_social }}</span>
              <span class="empresa-item__ruc">{{ emp.ruc }}</span>
            </div>
            <span class="empresa-item__fecha">{{ emp.fecha_registro }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Fila: facturación automática + estado de empresas -->
    <div class="seccion-fila">
      <!-- Facturación automática -->
      <div class="tarjeta">
        <div class="tarjeta__cab">
          <i class="bi bi-robot texto-morado"></i>
          <h2 class="tarjeta__titulo">Cobros automáticos</h2>
        </div>
        <p class="dash-subtitulo">
          Genera registros de cobro pendiente para suscripciones activas que vencen en los próximos 15 días.
          Podrás revisar y aprobar cada cobro en la sección de Pagos.
        </p>
        <div class="cobros-estado" v-if="resultadoCobros">
          <i :class="resultadoCobros.exito ? 'bi bi-check-circle-fill texto-verde' : 'bi bi-x-circle-fill texto-rojo'"></i>
          <span>{{ resultadoCobros.mensaje }}</span>
          <span v-if="resultadoCobros.errores?.length" class="texto-rojo" style="font-size:.78rem;">
            ({{ resultadoCobros.errores.length }} error(es))
          </span>
        </div>
        <div style="display:flex; gap:.75rem; flex-wrap:wrap;">
          <button class="btn btn--primario" @click="generarCobros" :disabled="generandoCobros">
            <i class="bi" :class="generandoCobros ? 'bi-arrow-clockwise spin' : 'bi-play-fill'"></i>
            {{ generandoCobros ? 'Procesando...' : 'Generar cobros ahora' }}
          </button>
          <router-link to="/facturacion/pagos" class="btn btn--secundario">
            <i class="bi bi-list-check"></i> Ver cobros pendientes
          </router-link>
        </div>
        <div class="cobros-resumen" v-if="kpis.pagos_pendientes > 0">
          <i class="bi bi-clock-history texto-warning"></i>
          <span>{{ kpis.pagos_pendientes }} cobro(s) pendiente(s) de verificación</span>
        </div>
      </div>

      <!-- Estado de empresas -->
      <div class="tarjeta">
        <div class="tarjeta__cab">
          <i class="bi bi-building texto-azul"></i>
          <h2 class="tarjeta__titulo">Estado de empresas</h2>
        </div>
        <div class="estado-empresas-chips">
          <div class="estado-chip-grande estado-chip-grande--verde">
            <span class="estado-chip-grande__num">{{ kpis.empresas_activas ?? '-' }}</span>
            <span class="estado-chip-grande__label">Activas</span>
          </div>
          <div class="estado-chip-grande estado-chip-grande--amarillo">
            <span class="estado-chip-grande__num">{{ kpis.empresas_suspendidas ?? '-' }}</span>
            <span class="estado-chip-grande__label">Suspendidas</span>
          </div>
          <div class="estado-chip-grande estado-chip-grande--rojo">
            <span class="estado-chip-grande__num">{{ kpis.empresas_cerradas ?? '-' }}</span>
            <span class="estado-chip-grande__label">Cerradas</span>
          </div>
          <div class="estado-chip-grande estado-chip-grande--gris">
            <span class="estado-chip-grande__num">{{ kpis.empresas_total ?? '-' }}</span>
            <span class="estado-chip-grande__label">Total</span>
          </div>
        </div>
        <div v-if="empresasSuspendidasDetalle.length" class="suspendidas-lista">
          <div class="tarjeta__cab" style="margin-top:.5rem;">
            <i class="bi bi-exclamation-triangle-fill texto-warning" style="font-size:.85rem;"></i>
            <span style="font-size:.8rem; font-weight:600; color:var(--text-secondary);">Empresas suspendidas</span>
          </div>
          <div v-for="emp in empresasSuspendidasDetalle" :key="emp.ruc" class="suspendida-item">
            <div>
              <div class="tabla__empresa">{{ emp.razon_social }}</div>
              <div class="tabla__ruc">{{ emp.ruc }}</div>
            </div>
            <div style="text-align:right;">
              <div style="font-size:.75rem; color:var(--text-secondary);">{{ emp.plan_nombre || 'Sin plan' }}</div>
              <span class="estado-chip chip--rojo">{{ emp.estado_suscripcion || 'suspendida' }}</span>
            </div>
          </div>
        </div>
        <div v-else-if="kpis.empresas_suspendidas === 0" class="texto-vacio" style="padding:.75rem 0;">
          <i class="bi bi-check-circle texto-verde"></i> Sin empresas suspendidas
        </div>
      </div>
    </div>

    <!-- Sección: recursos del servidor -->
    <div class="tarjeta tarjeta--ancha">
      <div class="tarjeta__cab">
        <i class="bi bi-hdd-rack texto-primario"></i>
        <h2 class="tarjeta__titulo">Recursos del servidor</h2>
        <span class="tarjeta__meta" v-if="recursos.servidor?.php_version">PHP {{ recursos.servidor.php_version }}</span>
        <button class="btn btn--xs btn--secundario" style="margin-left:auto;" @click="cargarRecursos" :disabled="cargandoRecursos">
          <i class="bi bi-arrow-clockwise" :class="{ spin: cargandoRecursos }"></i>
        </button>
      </div>
      <div v-if="!recursos.cpu && !cargandoRecursos" class="texto-vacio">
        Haz clic en actualizar para cargar las métricas del servidor.
      </div>
      <div v-else class="recursos-grid">
        <!-- CPU -->
        <div class="recurso-item">
          <div class="recurso-item__cab">
            <i class="bi bi-cpu texto-azul"></i>
            <span class="recurso-item__nombre">CPU</span>
            <span class="recurso-item__valor">{{ recursos.cpu?.porcentaje ?? '—' }}%</span>
          </div>
          <div class="barra-contenedor" style="height:.6rem;">
            <div class="barra-relleno" :style="{ width: (recursos.cpu?.porcentaje || 0) + '%', background: barraColor(recursos.cpu?.porcentaje) }"></div>
          </div>
          <div class="recurso-item__detalle">
            <span>Carga 1m: {{ recursos.cpu?.carga_1m ?? '—' }}</span>
            <span>Núcleos: {{ recursos.cpu?.nucleos ?? '—' }}</span>
          </div>
        </div>
        <!-- RAM -->
        <div class="recurso-item">
          <div class="recurso-item__cab">
            <i class="bi bi-memory texto-morado"></i>
            <span class="recurso-item__nombre">RAM</span>
            <span class="recurso-item__valor">{{ recursos.ram?.porcentaje ?? '—' }}%</span>
          </div>
          <div class="barra-contenedor" style="height:.6rem;">
            <div class="barra-relleno" :style="{ width: (recursos.ram?.porcentaje || 0) + '%', background: barraColor(recursos.ram?.porcentaje) }"></div>
          </div>
          <div class="recurso-item__detalle">
            <span>Usada: {{ formatBytes(recursos.ram?.usada) }}</span>
            <span>Total: {{ formatBytes(recursos.ram?.total) }}</span>
          </div>
        </div>
        <!-- Disco -->
        <div class="recurso-item">
          <div class="recurso-item__cab">
            <i class="bi bi-device-hdd texto-verde"></i>
            <span class="recurso-item__nombre">Disco</span>
            <span class="recurso-item__valor">{{ recursos.disco?.porcentaje ?? '—' }}%</span>
          </div>
          <div class="barra-contenedor" style="height:.6rem;">
            <div class="barra-relleno" :style="{ width: (recursos.disco?.porcentaje || 0) + '%', background: barraColor(recursos.disco?.porcentaje) }"></div>
          </div>
          <div class="recurso-item__detalle">
            <span>Usado: {{ formatBytes(recursos.disco?.usado) }}</span>
            <span>Libre: {{ formatBytes(recursos.disco?.libre) }}</span>
          </div>
        </div>
        <!-- Uptime -->
        <div class="recurso-item">
          <div class="recurso-item__cab">
            <i class="bi bi-clock-history texto-naranja"></i>
            <span class="recurso-item__nombre">Uptime</span>
          </div>
          <div class="recurso-uptime">{{ recursos.servidor?.uptime_formateado || '—' }}</div>
          <div class="recurso-item__detalle">
            <span>PHP: {{ formatBytes(recursos.servidor?.memoria_php) }}</span>
            <span>Límite: {{ recursos.servidor?.limite_php || '—' }}</span>
          </div>
        </div>
      </div>
      <p v-if="recursos.servidor && !recursos.servidor.es_linux" class="texto-vacio" style="font-size:.78rem; padding:.25rem 0;">
        <i class="bi bi-info-circle"></i> CPU y RAM solo disponibles en entorno Linux (producción).
      </p>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'AdminDashboard',
  data() {
    return {
      cargando: false,
      error: null,
      kpis: {},
      alertas: [],
      empresasPorVencer: [],
      distribucionPlanes: [],
      ultimosPagos: [],
      ingresosGrafica: [],
      ultimasEmpresas: [],
      estadosSuscripciones: [],
      empresasSuspendidasDetalle: [],
      // Cobros automáticos
      generandoCobros: false,
      resultadoCobros: null,
      // Recursos del servidor
      cargandoRecursos: false,
      recursos: {},
    }
  },
  mounted() {
    this.cargarDatos()
    this.cargarRecursos()
  },
  methods: {
    async cargarDatos() {
      this.cargando = true; this.error = null
      try {
        const res = await api.get('/dashboard/stats')
        const d = res.data?.datos || {}
        this.kpis                       = d.kpis || {}
        this.alertas                    = d.alertas || []
        this.empresasPorVencer          = d.empresas_por_vencer || []
        this.distribucionPlanes         = d.distribucion_planes || []
        this.ultimosPagos               = d.ultimos_pagos || []
        this.ingresosGrafica            = d.ingresos_grafica || []
        this.ultimasEmpresas            = d.ultimas_empresas || []
        this.estadosSuscripciones       = d.estados_suscripciones || []
        this.empresasSuspendidasDetalle = d.empresas_suspendidas_detalle || []
      } catch (e) {
        this.error = 'Error al cargar el dashboard: ' + (e.response?.data?.mensaje || e.message)
      } finally { this.cargando = false }
    },
    async cargarRecursos() {
      this.cargandoRecursos = true
      try {
        const res = await api.get('/dashboard/recursos-servidor')
        this.recursos = res.data?.datos || {}
      } catch (e) {
        this.recursos = {}
      } finally { this.cargandoRecursos = false }
    },
    async generarCobros() {
      this.generandoCobros = true
      this.resultadoCobros = null
      try {
        const res = await api.post('/dashboard/generar-cobros-automaticos')
        this.resultadoCobros = res.data
        // Recargar KPIs para reflejar los nuevos pagos pendientes
        await this.cargarDatos()
      } catch (e) {
        this.resultadoCobros = {
          exito: false,
          mensaje: e.response?.data?.mensaje || 'Error al generar cobros.',
        }
      } finally { this.generandoCobros = false }
    },
    formatear(v) { return parseFloat(v || 0).toFixed(2) },
    formatearCorto(v) {
      const n = parseFloat(v || 0)
      if (n >= 1000) return (n / 1000).toFixed(1) + 'k'
      return n.toFixed(0)
    },
    formatBytes(b) {
      if (!b) return '—'
      const n = parseInt(b)
      if (n >= 1073741824) return (n / 1073741824).toFixed(1) + ' GB'
      if (n >= 1048576)    return (n / 1048576).toFixed(1) + ' MB'
      if (n >= 1024)       return (n / 1024).toFixed(1) + ' KB'
      return n + ' B'
    },
    barraColor(pct) {
      const p = parseFloat(pct) || 0
      if (p >= 90) return 'var(--accent-danger)'
      if (p >= 70) return 'var(--accent-warning)'
      return 'var(--accent-success)'
    },
    diasRestantesCss(dias) {
      if (dias <= 3)  return 'fecha-badge--critico'
      if (dias <= 7)  return 'fecha-badge--urgente'
      if (dias <= 15) return 'fecha-badge--proximo'
      return 'fecha-badge--ok'
    },
    estadoPagoCss(estado) {
      const m = { validado: 'chip--verde', pendiente: 'chip--amarillo', rechazado: 'chip--rojo', completado: 'chip--verde' }
      return m[estado] || 'chip--gris'
    },
    iconoAlerta(nivel) {
      const m = { critico: 'bi-x-octagon-fill', urgente: 'bi-exclamation-triangle-fill', info: 'bi-info-circle-fill' }
      return m[nivel] || 'bi-dot'
    },
    porcentajePlan(item) {
      const max = Math.max(...this.distribucionPlanes.map(p => parseInt(p.total_activos) || 0), 1)
      return Math.round((parseInt(item.total_activos) / max) * 100)
    },
    alturaBarra(total) {
      const max = Math.max(...this.ingresosGrafica.map(m => parseFloat(m.total) || 0), 1)
      return Math.round((parseFloat(total) / max) * 90)
    },
    iniciales(nombre) {
      return (nombre || '?').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()
    },
    colorAvatar(nombre) {
      const colores = ['#6366f1','#0ea5e9','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899']
      let h = 0
      for (const c of (nombre || '')) h = ((h << 5) - h) + c.charCodeAt(0)
      return colores[Math.abs(h) % colores.length]
    },
  },
}
</script>

<style scoped>
.dashboard  { padding:1.5rem; max-width:1400px; margin:0 auto; display:flex; flex-direction:column; gap:1.5rem; }
.dash-cabecera { display:flex; align-items:center; justify-content:space-between; }
.dash-titulo   { font-size:1.5rem; font-weight:700; color:var(--text-primary); margin:0 0 .25rem; }
.dash-subtitulo{ font-size:.875rem; color:var(--text-secondary); margin:0; }
.alerta        { padding:.75rem 1rem; border-radius:.5rem; display:flex; gap:.5rem; }
.alerta-error  { background:color-mix(in srgb,var(--accent-danger) 10%,transparent); color:var(--accent-danger); border:1px solid color-mix(in srgb,var(--accent-danger) 30%,transparent); }
.btn           { display:inline-flex; align-items:center; gap:.4rem; border:none; border-radius:.5rem; padding:.6rem 1.2rem; cursor:pointer; font-size:.875rem; font-weight:600; transition:all .15s; }
.btn:disabled  { opacity:.5; cursor:not-allowed; }
.btn--secundario { background:var(--bg-main); color:var(--text-primary); border:1px solid var(--border-main); }
.btn--secundario:hover { background:var(--border-main); }
.btn--xs       { padding:.3rem .75rem; font-size:.8rem; }

/* KPIs */
.kpis-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(200px,1fr)); gap:1rem; }
.kpi       { background:var(--secondary-bg-main); border:1px solid var(--border-main); border-radius:.75rem; padding:1rem 1.25rem; display:flex; align-items:center; gap:1rem; transition:box-shadow .2s; }
.kpi:hover { box-shadow:var(--shadow-md); }
.kpi--con-alerta { border-color:var(--accent-warning); }
.kpi__icono  { width:2.5rem; height:2.5rem; border-radius:.5rem; display:flex; align-items:center; justify-content:center; font-size:1.1rem; flex-shrink:0; }
.kpi__icono--azul   { background:color-mix(in srgb,#0ea5e9 15%,transparent); color:#0ea5e9; }
.kpi__icono--verde  { background:color-mix(in srgb,var(--accent-success) 15%,transparent); color:var(--accent-success); }
.kpi__icono--morado { background:color-mix(in srgb,#8b5cf6 15%,transparent); color:#8b5cf6; }
.kpi__icono--amarillo{ background:color-mix(in srgb,var(--accent-warning) 15%,transparent); color:var(--accent-warning); }
.kpi__icono--rojo   { background:color-mix(in srgb,var(--accent-danger) 15%,transparent); color:var(--accent-danger); }
.kpi__icono--naranja{ background:color-mix(in srgb,#f97316 15%,transparent); color:#f97316; }
.kpi__info   { display:flex; flex-direction:column; min-width:0; }
.kpi__valor  { font-size:1.4rem; font-weight:700; color:var(--text-primary); white-space:nowrap; }
.kpi__etiqueta { font-size:.78rem; color:var(--text-secondary); display:flex; align-items:center; gap:.3rem; flex-wrap:wrap; }
.kpi__variacion { font-size:.72rem; font-weight:700; padding:.1rem .35rem; border-radius:.25rem; }
.kpi__variacion.positivo { background:color-mix(in srgb,var(--accent-success) 15%,transparent); color:var(--accent-success); }
.kpi__variacion.negativo { background:color-mix(in srgb,var(--accent-danger) 15%,transparent); color:var(--accent-danger); }

/* Tarjetas */
.seccion-fila  { display:grid; grid-template-columns:1fr 1fr; gap:1.5rem; }
.tarjeta       { background:var(--secondary-bg-main); border:1px solid var(--border-main); border-radius:.75rem; padding:1.25rem; display:flex; flex-direction:column; gap:1rem; }
.tarjeta__cab  { display:flex; align-items:center; gap:.5rem; }
.tarjeta__titulo { font-size:1rem; font-weight:700; color:var(--text-primary); margin:0; }

/* Alertas lista */
.alertas-lista { display:flex; flex-direction:column; gap:.5rem; max-height:260px; overflow-y:auto; }
.alerta-item   { display:flex; align-items:flex-start; gap:.5rem; padding:.5rem .75rem; border-radius:.375rem; font-size:.825rem; }
.alerta-item--critico { background:color-mix(in srgb,var(--accent-danger) 10%,transparent); color:var(--accent-danger); }
.alerta-item--urgente { background:color-mix(in srgb,var(--accent-warning) 10%,transparent); color:var(--accent-warning); }
.alerta-item--info    { background:color-mix(in srgb,#0ea5e9 10%,transparent); color:#0ea5e9; }

/* Tabla general */
.tabla        { width:100%; border-collapse:collapse; font-size:.825rem; }
.tabla th     { text-align:left; padding:.5rem .75rem; font-size:.75rem; font-weight:600; color:var(--text-secondary); border-bottom:1px solid var(--border-main); }
.tabla td     { padding:.6rem .75rem; border-bottom:1px solid var(--border-main); color:var(--text-primary); }
.tabla tr:last-child td { border-bottom:none; }
.tabla__empresa { font-weight:600; }
.tabla__ruc     { font-size:.7rem; color:var(--text-muted); }
.tabla__monto   { font-weight:700; color:var(--accent-success); }
.tabla__fecha   { font-size:.78rem; color:var(--text-secondary); white-space:nowrap; }
.fecha-badge    { font-size:.72rem; font-weight:700; padding:.15rem .5rem; border-radius:.25rem; }
.fecha-badge--critico { background:color-mix(in srgb,var(--accent-danger) 15%,transparent); color:var(--accent-danger); }
.fecha-badge--urgente { background:color-mix(in srgb,var(--accent-warning) 15%,transparent); color:var(--accent-warning); }
.fecha-badge--proximo { background:color-mix(in srgb,#f97316 15%,transparent); color:#f97316; }
.fecha-badge--ok      { background:color-mix(in srgb,var(--accent-success) 15%,transparent); color:var(--accent-success); }
.estado-chip  { font-size:.72rem; font-weight:600; padding:.15rem .5rem; border-radius:999px; }
.chip--verde  { background:color-mix(in srgb,var(--accent-success) 15%,transparent); color:var(--accent-success); }
.chip--amarillo{ background:color-mix(in srgb,var(--accent-warning) 15%,transparent); color:var(--accent-warning); }
.chip--rojo   { background:color-mix(in srgb,var(--accent-danger) 15%,transparent); color:var(--accent-danger); }
.chip--gris   { background:color-mix(in srgb,var(--text-muted) 15%,transparent); color:var(--text-muted); }

/* Distribucion planes */
.distribucion-planes { display:flex; flex-direction:column; gap:.75rem; }
.plan-barra          { display:flex; flex-direction:column; gap:.3rem; }
.plan-barra__info    { display:flex; justify-content:space-between; }
.plan-barra__nombre  { font-size:.825rem; font-weight:600; color:var(--text-primary); }
.plan-barra__total   { font-size:.78rem; color:var(--text-secondary); }
.barra-contenedor    { height:.5rem; background:var(--border-main); border-radius:999px; overflow:hidden; }
.barra-relleno       { height:100%; border-radius:999px; transition:width .4s; }
.plan-barra__detalle { display:flex; gap:1rem; font-size:.72rem; color:var(--text-muted); }

/* Grafica de barras CSS */
.grafica-barras { display:flex; align-items:flex-end; gap:.5rem; height:160px; padding-top:1rem; }
.grafica-col    { display:flex; flex-direction:column; align-items:center; gap:.3rem; flex:1; height:100%; }
.grafica-barra-wrap { display:flex; flex-direction:column; align-items:center; justify-content:flex-end; flex:1; width:100%; }
.grafica-valor  { font-size:.65rem; color:var(--text-secondary); margin-bottom:.2rem; white-space:nowrap; }
.grafica-barra  { width:100%; max-width:2.5rem; background:var(--primary-main); border-radius:.25rem .25rem 0 0; min-height:4px; transition:height .4s; }
.grafica-mes    { font-size:.65rem; color:var(--text-muted); text-align:center; }

/* Ultimas empresas */
.empresas-lista { display:flex; flex-direction:column; gap:.5rem; }
.empresa-item   { display:flex; align-items:center; gap:.75rem; padding:.4rem 0; border-bottom:1px solid var(--border-main); }
.empresa-item:last-child { border-bottom:none; }
.empresa-item__avatar { width:2rem; height:2rem; border-radius:.375rem; display:flex; align-items:center; justify-content:center; font-size:.7rem; font-weight:700; color:#fff; flex-shrink:0; }
.empresa-item__info   { flex:1; min-width:0; }
.empresa-item__nombre { font-size:.825rem; font-weight:600; color:var(--text-primary); display:block; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.empresa-item__ruc    { font-size:.7rem; color:var(--text-muted); }
.empresa-item__fecha  { font-size:.72rem; color:var(--text-secondary); white-space:nowrap; }

/* Helpers */
.texto-vacio  { font-size:.825rem; color:var(--text-muted); text-align:center; padding:1.5rem 0; }
.texto-primario { color:var(--primary-main); }
.texto-warning  { color:var(--accent-warning); }
.texto-verde    { color:var(--accent-success); }
.texto-azul     { color:#0ea5e9; }
.texto-morado   { color:#8b5cf6; }
.spin { animation:rotar 1s linear infinite; display:inline-block; }
@keyframes rotar { to { transform:rotate(360deg); } }

@media (max-width:900px) {
  .seccion-fila { grid-template-columns:1fr; }
  .kpis-grid    { grid-template-columns:repeat(auto-fill,minmax(160px,1fr)); }
}

/* Botón primario */
.btn--primario { background:var(--primary-main); color:#fff; border:none; }
.btn--primario:hover { background:var(--primary-hover); }

/* Cobros automáticos */
.cobros-estado {
  display:flex; align-items:center; gap:.5rem;
  padding:.6rem .85rem; border-radius:.5rem; font-size:.85rem;
  background:color-mix(in srgb,var(--primary-main) 8%,transparent);
  border:1px solid color-mix(in srgb,var(--primary-main) 20%,transparent);
}
.cobros-resumen {
  display:flex; align-items:center; gap:.5rem;
  font-size:.82rem; color:var(--text-secondary);
}
.texto-rojo { color:var(--accent-danger); }
.texto-naranja { color:#f97316; }

/* Estado de empresas chips grandes */
.estado-empresas-chips {
  display:grid; grid-template-columns:repeat(4,1fr); gap:.75rem;
}
.estado-chip-grande {
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  padding:.85rem .5rem; border-radius:.65rem; border:1px solid transparent; gap:.2rem;
}
.estado-chip-grande__num   { font-size:1.7rem; font-weight:700; line-height:1; }
.estado-chip-grande__label { font-size:.72rem; font-weight:600; text-transform:uppercase; letter-spacing:.04em; }
.estado-chip-grande--verde   { background:color-mix(in srgb,var(--accent-success) 10%,transparent); color:var(--accent-success); border-color:color-mix(in srgb,var(--accent-success) 20%,transparent); }
.estado-chip-grande--amarillo{ background:color-mix(in srgb,var(--accent-warning) 10%,transparent); color:var(--accent-warning); border-color:color-mix(in srgb,var(--accent-warning) 20%,transparent); }
.estado-chip-grande--rojo    { background:color-mix(in srgb,var(--accent-danger) 10%,transparent);  color:var(--accent-danger);  border-color:color-mix(in srgb,var(--accent-danger) 20%,transparent); }
.estado-chip-grande--gris    { background:var(--bg-main); color:var(--text-secondary); border-color:var(--border-main); }

/* Empresas suspendidas detalle */
.suspendidas-lista { display:flex; flex-direction:column; gap:.4rem; max-height:200px; overflow-y:auto; }
.suspendida-item {
  display:flex; justify-content:space-between; align-items:center;
  padding:.5rem .6rem; border-radius:.5rem;
  background:color-mix(in srgb,var(--accent-danger) 5%,transparent);
  border:1px solid color-mix(in srgb,var(--accent-danger) 12%,transparent);
  font-size:.82rem;
}

/* Tarjeta ancha (recursos) */
.tarjeta--ancha { grid-column:1/-1; }
.tarjeta__meta { font-size:.78rem; color:var(--text-muted); background:var(--bg-main); padding:.15rem .5rem; border-radius:.3rem; }

/* Recursos del servidor */
.recursos-grid {
  display:grid; grid-template-columns:repeat(auto-fill,minmax(220px,1fr)); gap:1.25rem;
}
.recurso-item { display:flex; flex-direction:column; gap:.5rem; }
.recurso-item__cab {
  display:flex; align-items:center; gap:.5rem;
}
.recurso-item__nombre { font-size:.875rem; font-weight:600; color:var(--text-primary); flex:1; }
.recurso-item__valor  { font-size:1.1rem; font-weight:700; color:var(--text-primary); }
.recurso-item__detalle{ display:flex; justify-content:space-between; font-size:.72rem; color:var(--text-muted); }
.recurso-uptime { font-size:1.5rem; font-weight:700; color:var(--primary-main); }
</style>
